import { useEffect, useRef } from "react";
import { useAuth, AppRole } from "@/contexts/AuthContext";
import { useModuleAccessPreflight } from "@/hooks/useModuleAccessPreflight";
import { AccessDenied } from "@/components/AccessDenied";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";

const ATTENDANCE_FALLBACK_ROLES: AppRole[] = [
  "superadmin", "principal", "deputy_principal",
  "head_administration", "head_academic", "it_incharge", "security_man",
  "hod_cmt", "hod_pst", "hod_sw",
  "cr_cmt4", "cr_cmt5", "cr_cmt6",
  "cr_pst4", "cr_pst5", "cr_pst6",
  "cr_sw4", "cr_sw5", "cr_sw6",
];

/**
 * Runs an automated module-access preflight against `module_visibility` AND
 * the hard-coded fallback rules before rendering the Attendance route.
 * Shows AccessDenied (with a structured reason) when the check fails.
 */
export function AttendanceAccessGate({ children }: { children: React.ReactNode }) {
  const { roles } = useAuth();
  const { toast } = useToast();
  const preflight = useModuleAccessPreflight("attendance", roles);
  const announcedRef = useRef(false);

  useEffect(() => {
    if (preflight.loading || announcedRef.current) return;
    announcedRef.current = true;
    if (preflight.granted) {
      toast({
        title: "Attendance access verified",
        description: `Granted via ${preflight.source === "db" ? "module visibility rule" : "fallback role"}: ${preflight.matchedRoles.join(", ")}`,
      });
    } else {
      toast({
        title: "Attendance access blocked",
        description: preflight.hasDbRules
          ? "Your roles are not enabled in module_visibility and don't match any fallback role."
          : "No module_visibility rules found and your roles aren't in the fallback list.",
        variant: "destructive",
      });
    }
  }, [preflight.loading, preflight.granted, preflight.source, preflight.hasDbRules, preflight.matchedRoles, toast]);

  if (preflight.loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="space-y-3 text-center">
          <Skeleton className="h-9 w-9 rounded-full mx-auto" />
          <p className="text-sm text-muted-foreground">Validating Attendance access…</p>
        </div>
      </div>
    );
  }

  if (!preflight.granted) {
    return (
      <AccessDenied
        pathname="/attendance"
        allowedRoles={(preflight.effectiveAllowedRoles.length
          ? preflight.effectiveAllowedRoles
          : ATTENDANCE_FALLBACK_ROLES) as AppRole[]}
        userRoles={roles}
      />
    );
  }

  return <>{children}</>;
}
